import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, OnChanges, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { async, Observable, throwError, concat, of, from, Subscription } from 'rxjs';
import { map, switchMap, concatMap } from 'rxjs/operators';
import { TreeNode } from 'primeng/api';
import { Router } from '@angular/router';

import { ProductGroup } from '../../shared/product-group';
import { RestApiService } from '../../services/rest-api.service';
import { ProductApiService } from '../../services/product-api.service';

/**
 * Reusable product-group selector backed by `ProductApiService`.
 *
 * When `prodGroupId` changes via `@Input`, the component fetches the matching `ProductGroup` record
 * and immediately emits it via `selectedGroup`. If `prodGroupId` is falsy, a placeholder "Undefined
 * Group" object is set so the template always has a non-null selection to display.
 *
 * The user can open the group-picker dialog (`displayGS`) via `onProductGroupButton`, which opens
 * `app-prodgroup-search`. Selecting a group in the dialog calls `loadProdGroup` with `emit=true`.
 *
 * **Output:** `selectedGroup` emits the resolved `ProductGroup` on every load (including initial
 * `prodGroupId` binding).
 */
@Component({
  selector: 'app-prodgroupselector',
  templateUrl: './prodgroupselector.component.html',
  styleUrls: ['./prodgroupselector.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class ProdgroupselectorComponent implements OnInit, OnChanges, OnDestroy {

  pgTreeNodes: TreeNode<ProductGroup>[] = [{}];
  currentNode: TreeNode<ProductGroup>;

  topmostPg: ProductGroup;
  childPgs: ProductGroup[];
  selectedPg: ProductGroup;

  selPgQuery: Subscription;
  topPgQuery: Subscription;
  childPgQuery: Subscription;

  displayGS: boolean;
  selectedGroupId: number;

  /** Label text displayed above the selector field. */
  @Input() labelKey: string;

  /** Pre-selection seed: a change here fetches the group and emits `selectedGroup`. */
  @Input() prodGroupId: number;

  /** When `true`, the selector is rendered as read-only. */
  @Input() disabled: boolean = false;

  /** Emits the resolved `ProductGroup` whenever a group is loaded or selected. */
  @Output()
  public selectedGroup: EventEmitter<ProductGroup> = new EventEmitter();

  constructor(
    private prodApi: ProductApiService,
    private router: Router,
  ) { }

  /**
   * Loads the product group for `pg` ID, emits it via `selectedGroup`.
   * If `pg` is falsy, sets a placeholder "Undefined Group" object.
   * @param pg - `ProductGroup.PGROUPID` to resolve.
   */
  loadProdGroup(pg: number): void {

    if (pg) {
      console.log('*** ProdgroupselectorComponent loadProdGroup: ===> group: ' + pg);
      this.selPgQuery = this.prodApi.getProdGroupById(pg)
        .pipe(
          map((data) => {
            this.selectedPg = data;
            if (this.selectedPg) {
              this.selectedGroup.emit(this.selectedPg);
            }
          })
        )
        .subscribe(() => {
          if (this.selectedPg) {
            console.log('*** ProdgroupselectorComponent loadProdGroup: ===> getSelectedPg 1: ' + JSON.stringify(this.selectedPg));
          }
        });
    } else {

      /* Just fill something
       */

      this.selectedPg = {

        PGROUPID: 0,
        PGUPPERID: 0,
        PGROUPCODE: '',
        PGROUPNAME: 'Undefined Group',
        PGROUPLEVEL: '',
        PGONLINE: false,
        PGSTOCKCODE: 0

      };
    }
  }

  /** Reacts to `prodGroupId` input changes by loading the new group. */
  ngOnChanges(changes: SimpleChanges) {
    for (let propName in changes) {
      let change = changes[propName];
      console.log('*** ProdgroupselectorComponent ngOnChanges (actual): ' + propName + ' ' + JSON.stringify(change.currentValue));

      switch (propName) {
        case 'prodGroupId':
          this.loadProdGroup(change.currentValue);
          break;
      }
    }
  }

  /** Unsubscribes all active HTTP subscriptions to prevent memory leaks. */
  ngOnDestroy(): void {
    if (this.selPgQuery) { this.selPgQuery.unsubscribe(); }

    if (this.topPgQuery) { this.topPgQuery.unsubscribe(); }

    if (this.childPgQuery) { this.childPgQuery.unsubscribe(); }
  }

  /** Lifecycle hook — no initialization logic; load is driven by `prodGroupId` input changes. */
  ngOnInit(): void {
  }

  /** Opens the `app-prodgroup-search` dialog, seeding it with the currently selected group's ID. */
  onProductGroupButton(event) {
    console.log('*** ProdgroupselectorComponent onProductGroupButton  ' + JSON.stringify(event));
    this.selectedGroupId = this.selectedPg ? this.selectedPg.PGROUPID : null;
    this.displayGS = true;
  }

  /** Receives the group selected from the dialog and loads it, emitting `selectedGroup`. */
  onSelectedGroup(event) {
    console.log('*** ProdgroupselectorComponent onSelectedGroup  ' + JSON.stringify(event));

    this.loadProdGroup(event.PGROUPID);
  }

  /** Navigates to the products list route filtered to the currently selected group. */
  onOpenProductGroup(event) {
    console.log('*** ProdgroupselectorComponent onOpenProductGroup  ' + JSON.stringify(event));
    if (this.selectedPg) {
      this.router.navigate(['/products', this.selectedPg.PGROUPID]);
    }

  }

}
